import { Injectable, NgZone } from '@angular/core';
import { HttpTransportType, HubConnection, HubConnectionBuilder, HubConnectionState } from '@microsoft/signalr';
import { Observable, Subject } from 'rxjs';
import { environment } from '../../../environments/environment';
import { Recado } from '../../interfaces/recados.dto';
import { UserService } from '../user/user.service';
import { LocalStorageService } from '../local-storage/local-storage.service';

const EVENTO = 'RecadoNovo';

/**
 * Tempo real do chat médico. No máximo uma conexão por aba: se o dashboard
 * empresta a dele (usarConexao), a própria é fechada e só volta quando ele solta.
 */
@Injectable({ providedIn: 'root' })
export class RecadosHubService {
  private readonly novoSubject = new Subject<Recado>();
  readonly novo$: Observable<Recado> = this.novoSubject.asObservable();

  private propria: HubConnection | null = null;
  private emprestada: HubConnection | null = null;
  private querConectar = false;
  private loginDaPropria: string | null = null;

  constructor(private userService: UserService, private ngZone: NgZone, private localStorage: LocalStorageService) {}

  async conectar(): Promise<void> {
    this.querConectar = true;
    const login = this.localStorage.get('login') ?? null;
    // Logout que não passou pelo cabeçalho (token expirado, guard) deixaria o próximo login no grupo do anterior.
    if (this.propria && this.loginDaPropria !== login) {
      const antiga = this.propria;
      this.propria = null;
      antiga.off(EVENTO);
      antiga.stop().catch(() => {});
    }
    if (this.emprestada || this.propria) return;
    const conn = this.criarConexao();
    this.propria = conn;
    this.loginDaPropria = login;
    this.registrar(conn);
    conn.onreconnected(() => { if (this.propria === conn) this.entrar(conn); });
    // Fechamento fora do alcance do withAutomaticReconnect: reabre como o dashboard.
    conn.onclose(() => {
      if (this.propria !== conn) return;
      setTimeout(() => {
        if (this.propria === conn && this.querConectar && !this.emprestada) this.iniciar(conn);
      }, 5000);
    });
    await this.iniciar(conn);
  }

  async usarConexao(conn: HubConnection): Promise<void> {
    if (this.emprestada !== conn) {
      if (this.emprestada) this.emprestada.off(EVENTO);
      this.emprestada = conn;
      this.registrar(conn);
      const propria = this.propria;
      this.propria = null;
      if (propria) {
        propria.off(EVENTO);
        propria.stop().catch(() => {});
      }
    }
    // O grupo se perde a cada reconexão do dashboard: reentra sempre.
    await conn.invoke('EntrarRecados');
  }

  liberarConexao(conn: HubConnection): void {
    if (this.emprestada !== conn) return;
    conn.off(EVENTO);
    this.emprestada = null;
    if (this.querConectar) this.conectar().catch(() => {});
  }

  async desconectar(): Promise<void> {
    this.querConectar = false;
    const conn = this.propria;
    this.propria = null;
    if (!conn) return;
    conn.off(EVENTO);
    try { await conn.stop(); } catch {}
  }

  protected criarConexao(): HubConnection {
    return new HubConnectionBuilder()
      .withUrl(environment.hubUrl, {
        skipNegotiation: true,
        transport: HttpTransportType.WebSockets,
        accessTokenFactory: () => this.userService.getTokenAuth(),
      })
      .withAutomaticReconnect({ nextRetryDelayInMilliseconds: () => 5000 })
      .build();
  }

  private async iniciar(conn: HubConnection): Promise<void> {
    if (this.propria !== conn) return;
    if (conn.state !== HubConnectionState.Disconnected) return;
    try {
      await conn.start();
    } catch {
      setTimeout(() => {
        if (this.propria === conn && this.querConectar && !this.emprestada) this.iniciar(conn);
      }, 5000);
      return;
    }
    // Emprestaram outra conexão enquanto esta abria.
    if (this.propria !== conn) {
      conn.stop().catch(() => {});
      return;
    }
    await this.entrar(conn);
  }

  private async entrar(conn: HubConnection): Promise<void> {
    try { await conn.invoke('EntrarRecados'); } catch {}
  }

  private registrar(conn: HubConnection): void {
    conn.off(EVENTO);
    conn.on(EVENTO, (json: string) => {
      let recado: Recado;
      try { recado = JSON.parse(json); } catch { return; }
      if (!recado) return;
      this.ngZone.run(() => this.novoSubject.next(recado));
    });
  }
}
